import React from 'react'
import { VStack, HStack, Checkbox, Button, Tooltip, Text, Box } from '@chakra-ui/react'
import SearchResultCard from './SearchResultCard'
import { ITableHeader, EAlignEnum } from 'components/CkTable'
import SvgIcon from 'components/SvgIcon'

import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { IProcessWithRelations } from 'interfaces/process'

export interface IItemData {
  id: string | number
  title: React.ReactNode
  subtitle?: React.ReactNode
  leadingIcon: React.ReactNode
  isSelected?: boolean
  isEditor?: boolean
  originalItem?: IEsGlobalSearchItem | IProcessWithRelations
  // Actions embedded in data
  onToggleSelect?: () => void
  onClick?: () => void
  onQuickView?: () => void
  onShare?: () => void
  onArchive?: () => void
  onMakeCopy?: () => void
  onRemove?: () => void
  canShowShare?: boolean
  canShowMakeCopy?: boolean
  canShowArchive?: boolean
  canShowRemove?: boolean
}

export interface IBulkAction {
  label: string
  iconName: string
  onClick: () => void
  disabled?: boolean
  tooltip?: string
}

interface SearchResultsListProps {
  tableData: IItemData[]
  headerList: ITableHeader[]
  isManageMode?: boolean
  canManageActions?: boolean
  showHeader?: boolean
  totalSelectedItems?: number
  totalItems?: number
  onSelectAll?: () => void
  bulkActions?: IBulkAction[]
}

const SearchResultsList = ({
  tableData,
  headerList,
  isManageMode = false,
  canManageActions = false,
  showHeader = false,
  totalSelectedItems = 0,
  totalItems = 0,
  onSelectAll,
  bulkActions = []
}: SearchResultsListProps) => {
  const renderHeader = () => {
    if (!showHeader || !headerList || headerList.length === 0) return null

    return (
      <HStack
        width="full"
        justifyContent="space-between"
        alignItems="center"
        paddingX={4}
        paddingY={3}
        backgroundColor="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        borderRadius="8px"
        marginBottom={2}
      >
        <HStack spacing={4} flex={1} width="full">
          {isManageMode && (
            <Checkbox
              isChecked={totalSelectedItems === totalItems && totalItems > 0}
              isIndeterminate={totalSelectedItems < totalItems && totalSelectedItems > 0}
              onChange={() => onSelectAll?.()}
              paddingTop="8px"
            />
          )}
          {headerList.map((header, index) => {
            const align = header.align || EAlignEnum.LEFT
            const justifyContent = 
              align === EAlignEnum.RIGHT ? 'flex-end' : 
              align === EAlignEnum.CENTER ? 'center' : 
              'flex-start'
            
            return (
              <Box
                key={header.accessor || index}
                flex={typeof header.width === 'number' ? undefined : 1}
                width={header.width}
                minWidth={header.minWidth}
                display="flex"
                justifyContent={justifyContent}
              >
                {typeof header.Header === 'string' ? (
                  <Text fontWeight={600} fontSize="16px" color="gray.700">
                    {header.Header}
                  </Text>
                ) : typeof header.Header === 'function' ? (
                  header.Header()
                ) : (
                  header.Header
                )}
              </Box>
            )
          })}
        </HStack>
        {isManageMode && totalSelectedItems > 0 && bulkActions.length > 0 && (
          <HStack spacing={2} flexShrink={0}>
            {bulkActions.map((action, index) => {
              const button = (
                <Button
                  key={index}
                  variant="outline"
                  background="transparent"
                  border="none"
                  borderRadius="8px"
                  color="gray.700"
                  disabled={action.disabled}
                  _hover={{ background: 'whiteAlpha.700' }}
                  _active={{ background: 'whiteAlpha.700' }}
                  onClick={action.onClick}
                  rightIcon={<SvgIcon size={16} iconName={action.iconName} />}
                  fontWeight={500}
                  fontSize="16px"
                  lineHeight="24px"
                >
                  {action.label}
                </Button>
              )

              if (action.tooltip) {
                return (
                  <Tooltip
                    key={index}
                    isDisabled={false}
                    label={action.tooltip}
                    height="56px"
                    fontSize="14px"
                    padding={2}
                    shouldWrapChildren
                    background="gray.700"
                    placement="top"
                    color="white"
                    hasArrow
                    borderRadius="4px"
                  >
                    {button}
                  </Tooltip>
                )
              }

              return button
            })}
          </HStack>
        )}
      </HStack>
    )
  }

  return (
    <VStack width="full" align="stretch" spacing={0}>
      {renderHeader()}
      <VStack width="full" align="stretch" spacing={2}>
        {tableData.map((itemData: IItemData, index: number) => {
          if (!itemData.originalItem) {
            console.warn(`SearchResultCard: originalItem is missing for item ${itemData.id}`)
            return null
          }
          return (
            <SearchResultCard
              key={itemData.id || index}
              item={itemData.originalItem}
              title={itemData.title}
              subtitle={itemData.subtitle}
              leadingIcon={itemData.leadingIcon}
              isManageMode={isManageMode}
              canManageActions={canManageActions}
              isSelected={itemData.isSelected || false}
              isEditor={itemData.isEditor || false}
              onToggleSelect={itemData.onToggleSelect || (() => {})}
              onClick={itemData.onClick || (() => {})}
              onQuickView={itemData.onQuickView || (() => {})}
              onShare={itemData.canShowShare && itemData.onShare ? itemData.onShare : undefined}
              onArchive={itemData.canShowArchive && itemData.onArchive ? itemData.onArchive : undefined}
              onMakeCopy={itemData.canShowMakeCopy && itemData.onMakeCopy ? itemData.onMakeCopy : undefined}
              onRemove={itemData.canShowRemove && itemData.onRemove ? itemData.onRemove : undefined}
              canShowRemove={itemData.canShowRemove || false}
            />
          )
        })}
      </VStack>
    </VStack>
  )
}

export default SearchResultsList

